import { useCallback, useEffect, useState } from "react";
import { Loader2, LogIn, Download, Copy, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SEO from "@/components/SEO";

// Admin: late orders, their KEEP / CANCEL links, and the answers.
// Read only toward Shopify. Refunds are done by hand in Shopify.

const STATUS_LABEL = { keep: "KEEP", cancel: "CANCEL", no_reply: "No reply" };

const input =
  "w-full bg-transparent border border-white/10 px-4 py-3 text-sm font-['Inter'] text-white/80 outline-none focus:border-white/30 placeholder:text-white/20";
const primaryBtn =
  "bg-white text-black px-5 py-3 text-[11px] uppercase tracking-[0.2em] font-semibold hover:bg-white/90 transition-colors disabled:opacity-40 flex items-center justify-center gap-2";
const ghostBtn =
  "border border-white/15 text-white/80 px-4 py-3 text-[11px] uppercase tracking-[0.2em] font-semibold hover:border-white/40 transition-colors flex items-center justify-center gap-2";
const label = "text-[10px] uppercase tracking-[0.15em] text-white/40 font-['Inter']";

function formatEt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      timeZone: "America/New_York",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }) + " ET";
  } catch {
    return iso;
  }
}

function CopyButton({ text, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt("Copy this link:", text);
    }
  };
  return (
    <button type="button" data-testid={testid} onClick={copy} className="shrink-0 text-white/50 hover:text-white p-1" aria-label="Copy link">
      {copied ? <Check size={14} /> : <Copy size={14} />}
    </button>
  );
}

function LoginGate() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/login", { email, password });
      window.location.reload();
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="admin-late-orders-page" className="ph-no-capture bg-[#050505] min-h-screen flex items-center justify-center px-6">
      <SEO title="Late Orders | AERWEAR" description="AERWEAR account and order tools." noindex />
      <form onSubmit={submit} className="w-full max-w-xs space-y-4">
        <h1 className="text-2xl tracking-[0.12em] uppercase text-white text-center mb-6">Admin Access</h1>
        {error && <p className="text-sm text-red-400/70 font-['Inter'] text-center">{error}</p>}
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Admin Email" className={input} />
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={input} />
        <button type="submit" disabled={busy} className={`${primaryBtn} w-full`}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <LogIn size={14} />} Sign In
        </button>
      </form>
    </div>
  );
}

export default function AdminLateOrdersPage() {
  const { user } = useAuth();
  const isAdmin = user && user.role === "admin";

  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [numbers, setNumbers] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");
  const [results, setResults] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/late-orders");
      setData(data);
      setLoadError("");
    } catch (err) {
      setLoadError(formatApiError(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  if (!isAdmin) return <LoginGate />;

  const addOrders = async (e) => {
    e.preventDefault();
    const list = numbers.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (!list.length) return;
    setAdding(true);
    setAddError("");
    setResults([]);
    try {
      const { data } = await api.post("/admin/late-orders/links", { order_numbers: list });
      setResults(data.results);
      if (data.results.every((r) => r.ok)) setNumbers("");
      load();
    } catch (err) {
      setAddError(formatApiError(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const download = async (type) => {
    try {
      const res = await api.get("/admin/late-orders/export.csv", { params: { type }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = type === "links" ? "aerwear-late-orders-links.csv" : "aerwear-late-orders-answers.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setLoadError("Download failed. Check that LATE_ORDER_SECRET is set, then try again.");
    }
  };

  const remove = async (n) => {
    if (!window.confirm(`Take order #${n} off the late list? Its answer, if any, is deleted too.`)) return;
    try {
      await api.delete(`/admin/late-orders/${n}`);
      load();
    } catch (err) {
      setLoadError(formatApiError(err.response?.data?.detail));
    }
  };

  const orders = data?.orders || [];
  const passed = data?.deadline_passed;
  const shown = orders.filter((o) =>
    filter === "all" ? true : filter === "refund" ? o.on_refund_list : o.status === filter
  );
  const tabs = [
    ["all", `All ${data?.counts.total ?? 0}`],
    ["keep", `Keep ${data?.counts.keep ?? 0}`],
    ["cancel", `Cancel ${data?.counts.cancel ?? 0}`],
    ["no_reply", `No reply ${data?.counts.no_reply ?? 0}`],
    ...(passed ? [["refund", `Refund list ${orders.filter((o) => o.on_refund_list).length}`]] : []),
  ];

  return (
    <div data-testid="admin-late-orders-page" className="ph-no-capture bg-[#050505] min-h-screen">
      <SEO title="Late Orders | AERWEAR" description="AERWEAR account and order tools." noindex />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 md:py-16 font-['Inter']">
        <p className="text-[10px] uppercase tracking-[0.3em] text-white/30 mb-2">Admin Panel</p>
        <h1 className="text-3xl md:text-4xl tracking-[0.1em] uppercase text-white">Late Orders</h1>

        {data && (
          <p data-testid="late-deadline" className="mt-4 text-sm text-white/60 leading-relaxed">
            {passed
              ? `Answers closed ${data.deadline_label}. CANCEL and no reply orders are the refund list. Refund them by hand in Shopify.`
              : `Answers close ${data.deadline_label}. A customer's last answer before then counts.`}
          </p>
        )}
        {data && !data.secret_set && (
          <p data-testid="late-secret-missing" className="mt-4 text-sm text-red-300/80 border border-red-300/30 p-3">
            LATE_ORDER_SECRET is not set on the server, so links can't be made yet. Set it in Emergent, then publish.
          </p>
        )}
        {loadError && <p className="mt-4 text-sm text-red-300/80">{loadError}</p>}

        {/* Add late orders */}
        <form onSubmit={addOrders} className="mt-8 p-4 sm:p-5 border border-white/10 bg-white/[0.02] space-y-3">
          <label htmlFor="late-numbers" className={label}>Add late orders</label>
          <textarea
            id="late-numbers"
            data-testid="late-numbers-input"
            value={numbers}
            onChange={(e) => setNumbers(e.target.value)}
            placeholder="Shopify order numbers, e.g. 1234, 1235"
            className={`${input} h-24 resize-y`}
          />
          <p className="text-xs text-white/40">Each number is checked in Shopify (read only). Cancelled and fulfilled orders are skipped.</p>
          <button type="submit" data-testid="late-add-btn" disabled={adding} className={`${primaryBtn} w-full sm:w-auto`}>
            {adding && <Loader2 size={14} className="animate-spin" />} Check in Shopify and make links
          </button>
          {addError && <p className="text-sm text-red-300/80">{addError}</p>}
          {results.length > 0 && (
            <ul data-testid="late-add-results" className="pt-2 space-y-3">
              {results.map((r) => (
                <li key={r.order_number} className="text-sm border-t border-white/5 pt-3">
                  <div className="flex flex-wrap gap-x-2 text-white/80">
                    <span className="font-semibold">#{r.order_number}</span>
                    {r.ok ? <span className="text-white/60">{r.first_name} {r.email}</span> : <span className="text-red-300/80">{r.error}</span>}
                  </div>
                  {r.warning && <p className="text-amber-200/80 text-xs mt-1">{r.warning}</p>}
                  {r.ok && (
                    <div className="mt-2 space-y-1 text-xs">
                      {[["Keep", r.keep_link], ["Cancel", r.cancel_link]].map(([name, link]) => (
                        <div key={name} className="flex items-center gap-2 min-w-0">
                          <span className="w-12 shrink-0 text-white/40">{name}</span>
                          <span className="truncate text-white/60">{link}</span>
                          <CopyButton text={link} testid={`copy-${name.toLowerCase()}-${r.order_number}`} />
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </form>

        {/* Downloads */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button type="button" data-testid="late-csv-links" onClick={() => download("links")} className={ghostBtn}>
            <Download size={14} /> Links CSV (mail merge)
          </button>
          <button type="button" data-testid="late-csv-results" onClick={() => download("results")} className={ghostBtn}>
            <Download size={14} /> Answers CSV
          </button>
        </div>

        {/* List */}
        <div className="mt-10 flex flex-wrap gap-2" role="tablist">
          {tabs.map(([key, text]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={filter === key}
              data-testid={`late-tab-${key}`}
              onClick={() => setFilter(key)}
              className={`px-3 py-2 text-[11px] uppercase tracking-[0.15em] border transition-colors ${
                filter === key ? "bg-white text-black border-white" : "border-white/15 text-white/70 hover:border-white/40"
              }`}
            >
              {text}
            </button>
          ))}
        </div>

        <ul data-testid="late-list" className="mt-4 divide-y divide-white/5 border-y border-white/5">
          {!data && !loadError && (
            <li className="py-6 flex justify-center"><Loader2 size={18} className="animate-spin text-white/40" /></li>
          )}
          {data && shown.length === 0 && <li className="py-6 text-sm text-white/40">No orders here.</li>}
          {shown.map((o) => (
            <li key={o.order_number} data-testid={`late-row-${o.order_number}`} className="py-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm text-white">
                  <span className="font-semibold">#{o.order_number}</span>
                  <span className="text-white/60"> {o.first_name}</span>
                </p>
                <p className="text-xs text-white/50 truncate">{o.email || "No email"}</p>
                {o.chosen_at && (
                  <p className="text-xs text-white/40 mt-1">
                    {formatEt(o.chosen_at)}{o.changes > 0 ? `, changed ${o.changes}x` : ""}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <span
                  className={`text-[11px] uppercase tracking-[0.15em] px-2 py-1 border ${
                    o.status === "keep" ? "border-white/60 text-white" : o.status === "cancel" ? "bg-white text-black border-white" : "border-white/15 text-white/50"
                  }`}
                >
                  {STATUS_LABEL[o.status]}
                </span>
                {o.on_refund_list && <span className="text-[10px] uppercase tracking-[0.15em] text-white/60">Refund</span>}
                <button type="button" onClick={() => remove(o.order_number)} className="text-[10px] uppercase tracking-[0.15em] text-white/30 hover:text-white/70">
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
